import React from 'react';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { AnimatedViews, useResponsive } from '@garnish/core';

import { OrdersList } from '@order/features-orders';

// ─────────────────────────────────────────────────────────────────────────────

export const OrdersListLoading = () => {
  const placeholderIndexes = [...Array(PLACEHOLDER_ORDERS).keys()];

  return (
    <>
      {placeholderIndexes.map((index) => (
        <OrderPlaceholder key={index} index={index} />
      ))}
    </>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const OrderPlaceholder = (props: OrderPlaceholderProps) => {
  const { index } = props;

  const { match } = useResponsive();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const horizontalSpacingStyle = match([styles.horizontalSpacingXS, styles.horizontalSpacingSM]);
  const headerStyles = [styles.header, horizontalSpacingStyle];
  const lineItemStyles = [styles.lineItem, horizontalSpacingStyle];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <OrdersList.OrderContainer>
      <AnimatedViews.Pulse index={index}>
        <View style={headerStyles}>
          <View style={styles.date} />

          <View style={styles.location} />

          <View style={styles.viewDetailsButton} />
        </View>

        <OrdersList.OrderLineItemsContainer>
          <View style={lineItemStyles}>
            <View style={styles.image} />

            <View style={styles.details}>
              <View style={styles.name} />

              <View style={styles.description} />
            </View>

            <View style={styles.reorderButton} />
          </View>
        </OrdersList.OrderLineItemsContainer>
      </AnimatedViews.Pulse>
    </OrdersList.OrderContainer>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PLACEHOLDER_ORDERS = 3;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['2'],
    paddingVertical: theme.spacing['4'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  horizontalSpacingXS: {
    paddingHorizontal: theme.spacing['4'],
  },
  horizontalSpacingSM: {
    paddingHorizontal: theme.spacing['6'],
  },
  date: {
    width: 60,
    height: 12,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  location: {
    width: 148,
    height: 12,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  viewDetailsButton: {
    width: 88,
    height: 26,
    marginLeft: 'auto',
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  lineItem: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['2'],
  },
  image: {
    width: 80,
    height: 80,
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  details: {
    flex: 1,
    rowGap: 4,
  },
  name: {
    width: '48%',
    height: 16,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  description: {
    width: '28%',
    height: 12,
    marginVertical: 3,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  reorderButton: {
    width: 32,
    height: 32,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderPlaceholderProps = {
  index: number;
};
